/**
 * loader.js — Avatutor Widget loader (F12.D §57.8).
 *
 * Script vanilla embebido por el comercio (sin Vue, sin Vuetify). Lee
 * atributos `data-*` del propio <script>, inyecta un FAB flotante y un
 * <iframe> apuntando al widget Vue (Embed.vue) servido en el mismo
 * dominio que este loader.
 *
 * El loader es el bridge entre el sitio host y el iframe:
 *   - Crea el iframe con allow="microphone; camera; autoplay".
 *   - Escucha postMessage del widget (kinds: widget:ready/open/close/
 *     call-ended) y los re-emite como CustomEvent en `document`, además
 *     de invocar callbacks registrados via window.AvatutorWidget.on().
 *
 * NO maneja LiveKit ni branding — eso vive en Embed.vue. NO toca el
 * DOM del host fuera del FAB + iframe (clases prefijadas `avt-` para
 * evitar colisiones de CSS).
 */
(function () {
  'use strict';

  if (window.AvatutorWidget) {
    console.warn('[avatutor] loader ya inicializado, ignorando segunda carga');
    return;
  }

  // --- 1. CONFIG: leer data-* del <script> que cargó este loader ---
  const SCRIPT_TAG = document.currentScript;
  if (!SCRIPT_TAG) {
    console.error('[avatutor] document.currentScript no disponible — loader debe cargarse con <script src=...>');
    return;
  }

  const cfg = {
    avatarId:    SCRIPT_TAG.dataset.avatarId,
    creatorSlug: SCRIPT_TAG.dataset.creatorSlug || '',
    listingId:   SCRIPT_TAG.dataset.listingId || '',
    position:    SCRIPT_TAG.dataset.position || 'bottom-right',
    theme:       SCRIPT_TAG.dataset.theme || 'auto',
    color:       SCRIPT_TAG.dataset.color || '',
    fabThumb:    SCRIPT_TAG.dataset.fabThumb || '',
    launch:      SCRIPT_TAG.dataset.launch || 'manual',
    // §16 — data-mobile-size: en mobile (<=480px) el widget abierto puede
    // ocupar toda la pantalla ('full', default = comportamiento actual),
    // un cuarto de pantalla anclado a la esquina ('quarter') o una tarjeta
    // aún más chica ('mini', ~180x260 — ver §18). No afecta desktop (ahí
    // manda data-position). Para data-position="fullscreen" el modo full
    // siempre gana (no tiene sentido un fullscreen en cuarto).
    mobileSize:  SCRIPT_TAG.dataset.mobileSize || 'full',
    // §modo guía (TASK-009) — data-mode="guide" activa el modo guía: el
    // avatar puede navegar la página anfitriona. Default '' = modo normal
    // (venta / informativo decidido por el backend). Se propaga como
    // ?mode=guide al iframe del widget.
    mode:        SCRIPT_TAG.dataset.mode || '',
    // data-origen — etiqueta para imputar el consumo de este embed (p. ej.
    // "kiosko-lo-barnechea"). Se propaga como ?origen= al iframe.
    origen:      SCRIPT_TAG.dataset.origen || '',
  };

  if (!cfg.avatarId) {
    console.error('[avatutor] data-avatar-id es obligatorio');
    return;
  }

  const VALID_POSITIONS = ['bottom-right', 'bottom-left', 'fullscreen', 'modal'];
  if (!VALID_POSITIONS.includes(cfg.position)) {
    console.warn(`[avatutor] data-position="${cfg.position}" inválido, usando bottom-right`);
    cfg.position = 'bottom-right';
  }

  const VALID_MOBILE_SIZES = ['full', 'quarter', 'mini'];
  if (!VALID_MOBILE_SIZES.includes(cfg.mobileSize)) {
    console.warn(`[avatutor] data-mobile-size="${cfg.mobileSize}" inválido, usando full`);
    cfg.mobileSize = 'full';
  }

  const VALID_MODES = ['', 'guide'];
  if (!VALID_MODES.includes(cfg.mode)) {
    console.warn(`[avatutor] data-mode="${cfg.mode}" inválido, ignorando`);
    cfg.mode = '';
  }

  const SCRIPT_ORIGIN = new URL(SCRIPT_TAG.src).origin;
  const IFRAME_URL = new URL(`/embed/${encodeURIComponent(cfg.avatarId)}`, SCRIPT_ORIGIN);
  if (cfg.creatorSlug) IFRAME_URL.searchParams.set('creator', cfg.creatorSlug);
  if (cfg.listingId)   IFRAME_URL.searchParams.set('listing', cfg.listingId);
  if (cfg.color)       IFRAME_URL.searchParams.set('color', cfg.color);
  if (cfg.theme && cfg.theme !== 'auto') IFRAME_URL.searchParams.set('theme', cfg.theme);
  if (cfg.mode)        IFRAME_URL.searchParams.set('mode', cfg.mode);
  if (cfg.origen)      IFRAME_URL.searchParams.set('origen', cfg.origen);
  // §18 — el modo mini necesita que la UI de adentro se dibuje más chica.
  // El loader solo puede achicar el iframe; el zoom del contenido lo aplica
  // Embed.vue, y solo cuando su propio viewport es realmente angosto (en
  // desktop este mismo embed abre a 380px y no debe escalar nada).
  if (cfg.mobileSize === 'mini') IFRAME_URL.searchParams.set('size', 'mini');
  // §15 — El autostart ahora es el comportamiento por defecto de Embed.vue
  // (no depende de un query param). El click en el FAB es el único gesto
  // del usuario: abre el iframe y Embed arranca la llamada directo en
  // onMounted, sin pantalla de pre-call intermedia. El permiso de micrófono
  // se pide una sola vez DENTRO del iframe (allow="microphone" delega el
  // gesto del parent).

  const BRAND_COLOR = cfg.color || '#00F0FF';
  const Z = 2147483647; // max int32 — sobre cualquier cosa del host

  // --- 2. STATE ---
  const listeners = { ready: [], open: [], close: [], 'call-ended': [], navigate: [] };
  let iframe = null;
  let fab = null;
  let backdrop = null;
  let styleTag = null;
  let isOpen = false;
  let isReady = false;
  let destroyed = false;

  // --- 3. STYLES (inline para evitar request extra + CSP friendly) ---
  function injectStyles() {
    if (styleTag) return;
    styleTag = document.createElement('style');
    styleTag.setAttribute('data-avt-loader', '');
    styleTag.textContent = `
      .avt-fab {
        position: fixed;
        ${cfg.position === 'bottom-left' ? 'left: 24px;' : 'right: 24px;'}
        bottom: 24px;
        width: 160px; height: 220px;
        border-radius: 20px;
        background-color: ${BRAND_COLOR};
        background-size: cover;
        background-position: center top;
        cursor: pointer;
        z-index: ${Z - 1};
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
        border: none;
        padding: 0;
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        text-align: left;
        position: fixed;
        transition: transform 0.18s ease, box-shadow 0.18s ease;
        animation: avt-fab-in 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      /* Sin foto conocida: color de marca liso, nunca la cara de otro avatar.
         Se quita cuando llega profile_image_url (ver applyFabThumb). */
      .avt-fab-nothumb { background-image: none; }
      .avt-fab:hover { transform: translateY(-3px); box-shadow: 0 18px 42px rgba(0, 0, 0, 0.42); }
      .avt-fab:active { transform: translateY(0); }
      .avt-fab.avt-hidden { display: none !important; }

      .avt-fab-overlay {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        padding: 36px 14px 14px;
        background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 80%);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        color: #fff;
      }
      .avt-fab-label {
        font-size: 14px;
        font-weight: 600;
        line-height: 1.2;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
      }
      .avt-fab-call {
        width: 30px; height: 30px;
        border-radius: 50%;
        background: ${BRAND_COLOR};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }
      .avt-fab-call svg { width: 16px; height: 16px; fill: #fff; }

      @media (max-width: 480px) {
        .avt-fab { width: 132px; height: 184px; border-radius: 16px; bottom: 16px; ${cfg.position === 'bottom-left' ? 'left: 16px;' : 'right: 16px;'} }
        .avt-fab-overlay { padding: 28px 12px 12px; }
        .avt-fab-label { font-size: 13px; }
        .avt-fab-call { width: 26px; height: 26px; }
      }

      @keyframes avt-fab-in {
        from { opacity: 0; transform: scale(0.6); }
        to { opacity: 1; transform: scale(1); }
      }

      .avt-iframe {
        position: fixed;
        z-index: ${Z};
        border: 0;
        background: #0a0e1a;
        display: none;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
      }
      .avt-iframe.avt-open { display: block; animation: avt-frame-in 0.24s ease-out; }

      .avt-iframe.avt-pos-bottom-right {
        right: 24px; bottom: 100px;
        width: 380px; height: 600px;
        max-width: calc(100vw - 32px);
        max-height: calc(100vh - 120px);
        border-radius: 16px;
      }
      .avt-iframe.avt-pos-bottom-left {
        left: 24px; bottom: 100px;
        width: 380px; height: 600px;
        max-width: calc(100vw - 32px);
        max-height: calc(100vh - 120px);
        border-radius: 16px;
      }
      .avt-iframe.avt-pos-fullscreen {
        inset: 0; width: 100vw; height: 100vh; height: 100dvh; border-radius: 0;
      }
      .avt-iframe.avt-pos-modal {
        top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        width: min(420px, calc(100vw - 32px));
        height: min(680px, calc(100vh - 64px));
        border-radius: 20px;
      }

      .avt-backdrop {
        position: fixed; inset: 0;
        background: rgba(0, 0, 0, 0.55);
        z-index: ${Z - 2};
        display: none;
        animation: avt-fade-in 0.18s ease-out;
      }
      .avt-backdrop.avt-open { display: block; }

      @keyframes avt-frame-in {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .avt-iframe.avt-pos-modal.avt-open {
        animation: avt-modal-in 0.24s ease-out;
      }
      @keyframes avt-modal-in {
        from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
        to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      }
      @keyframes avt-fade-in {
        from { opacity: 0; } to { opacity: 1; }
      }

      @media (max-width: 480px) {
        /* Modo full (default): el widget abierto ocupa toda la pantalla.
           100dvh (viewport dinámico) en vez de 100vh: en mobile el 100vh
           incluye el área detrás de la barra de URL/sistema, lo que hacía
           que el fondo del iframe (controles de llamada) quedara tapado por
           la barra inferior de Android y generara scroll. dvh sigue la zona
           visible real. Fallback a 100vh en browsers viejos. */
        .avt-iframe.avt-msize-full.avt-pos-bottom-right,
        .avt-iframe.avt-msize-full.avt-pos-bottom-left,
        .avt-iframe.avt-msize-full.avt-pos-modal {
          inset: 0; width: 100vw;
          height: 100vh; height: 100dvh;
          max-width: none; max-height: none;
          border-radius: 0;
          transform: none; top: 0; left: 0; right: 0; bottom: 0;
        }

        /* Modo quarter: el widget abierto queda como una tarjeta en la
           esquina (~un cuarto de pantalla), siguiendo data-position. Deja
           visible el sitio del host detrás. Respeta safe-area-inset para no
           quedar bajo la barra de gestos/navegación. */
        .avt-iframe.avt-msize-quarter.avt-pos-bottom-right,
        .avt-iframe.avt-msize-quarter.avt-pos-bottom-left,
        .avt-iframe.avt-msize-quarter.avt-pos-modal {
          top: auto; inset: auto;
          width: min(62vw, 240px);
          height: min(46dvh, 360px);
          max-width: none; max-height: none;
          border-radius: 16px;
          transform: none;
          bottom: calc(16px + env(safe-area-inset-bottom, 0px));
        }
        .avt-iframe.avt-msize-quarter.avt-pos-bottom-left {
          left: 12px; right: auto;
        }
        .avt-iframe.avt-msize-quarter.avt-pos-bottom-right,
        .avt-iframe.avt-msize-quarter.avt-pos-modal {
          right: 12px; left: auto;
        }

        /* §18 — Modo mini: misma idea que quarter (tarjeta en la esquina)
           pero ~180x260. La UI de adentro no se recorta: Embed.vue detecta
           el ?size=mini y aplica zoom para que el layout siga siendo el de
           quarter, solo que dibujado más chico. */
        .avt-iframe.avt-msize-mini.avt-pos-bottom-right,
        .avt-iframe.avt-msize-mini.avt-pos-bottom-left,
        .avt-iframe.avt-msize-mini.avt-pos-modal {
          top: auto; inset: auto;
          width: min(50vw, 180px);
          height: min(34dvh, 260px);
          max-width: none; max-height: none;
          border-radius: 14px;
          transform: none;
          bottom: calc(16px + env(safe-area-inset-bottom, 0px));
        }
        .avt-iframe.avt-msize-mini.avt-pos-bottom-left {
          left: 12px; right: auto;
        }
        .avt-iframe.avt-msize-mini.avt-pos-bottom-right,
        .avt-iframe.avt-msize-mini.avt-pos-modal {
          right: 12px; left: auto;
        }
      }
    `;
    document.head.appendChild(styleTag);
  }

  // Base del backend, inlineada por Vite al compilar el loader. Mismo valor
  // que usa el iframe del widget e INCLUYE `/api` (ver .env.production), así
  // que las rutas se arman sin repetir el prefijo. Si no está definida,
  // `fetchAvatarThumb` no hace nada y el FAB se queda con el icono neutro.
  const API_BASE = (import.meta.env && import.meta.env.VITE_API_BASE_URL)
    ? String(import.meta.env.VITE_API_BASE_URL).replace(/\/+$/, '')
    : '';

  function applyFabThumb(url) {
    if (!fab || !url) return;
    fab.style.backgroundImage = `url("${String(url).replace(/"/g, '%22')}")`;
    fab.classList.remove('avt-fab-nothumb');
  }

  /**
   * Pide la ficha pública del avatar y usa su `profile_image_url` en el FAB.
   * Best-effort: cualquier fallo (red, 404 del avatar, avatar sin foto) deja
   * el icono neutro. Nunca mostramos la foto de otro avatar como relleno.
   */
  function fetchAvatarThumb() {
    if (!API_BASE || !cfg.avatarId || typeof fetch !== 'function') return;
    fetch(`${API_BASE}/widget/avatars/${encodeURIComponent(cfg.avatarId)}`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.profile_image_url) applyFabThumb(data.profile_image_url);
      })
      .catch(() => { /* silencioso: el FAB ya tiene su icono neutro */ });
  }

  // --- 4. FAB (botón flotante toggle — pill estilo "iniciar llamada") ---
  function createFab() {
    if (fab) return;
    fab = document.createElement('button');
    fab.className = 'avt-fab';
    fab.setAttribute('type', 'button');
    fab.setAttribute('aria-label', 'Iniciar conversación con tutor virtual');

    // Thumb del FAB. Prioridad:
    //   1. `data-fab-thumb` explícito del host (control total del cliente).
    //   2. Mapa legacy por id, para no cambiarle la cara a los embeds que ya
    //      estaban en producción antes de que existiera la ficha pública.
    //   3. Icono neutro mientras se resuelve, y `profile_image_url` del avatar
    //      apenas responde `GET /api/widget/avatars/{id}` (ver applyFabThumb).
    // Antes el default de cualquier id sin mapeo era `Isabel-Allende.webp`, así
    // que todo avatar nuevo aparecía con la cara de otra persona.
    const THUMB_BY_AVATAR = {
      '2':  'Isabel-Allende.webp',
      '10': 'roberto.png',
      '11': 'iveis.png',
      '12': 'roberto.png',
      '13': 'roberto.png',
      '14': 'IMAGEN6.jpeg',
    };
    const legacyThumb = THUMB_BY_AVATAR[String(cfg.avatarId)];
    const initialThumb = cfg.fabThumb
      || (legacyThumb ? `${SCRIPT_ORIGIN}/${legacyThumb}` : '');
    if (initialThumb) applyFabThumb(initialThumb);
    else fab.classList.add('avt-fab-nothumb');

    // Sin thumb explícito ni mapeo legacy, preguntamos la foto real del avatar.
    // Es best-effort: si falla, el FAB se queda con el icono neutro.
    if (!cfg.fabThumb && !legacyThumb) fetchAvatarThumb();

    // Una sola forma: la foto como fondo + overlay con label + ícono call.
    fab.innerHTML =
      '<span class="avt-fab-overlay">' +
        '<span class="avt-fab-label">Iniciar conversación</span>' +
        '<span class="avt-fab-call" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.58l2.2-2.21c.28-.27.36-.66.25-1.01C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/></svg>' +
        '</span>' +
      '</span>';

    fab.addEventListener('click', toggle);
    document.body.appendChild(fab);
  }

  // --- 5. IFRAME (lazy-create al primer open) ---
  function createIframe() {
    if (iframe) return;
    iframe = document.createElement('iframe');
    iframe.className = `avt-iframe avt-pos-${cfg.position} avt-msize-${cfg.mobileSize}`;
    iframe.src = IFRAME_URL.toString();
    iframe.setAttribute('allow', 'microphone; camera; autoplay');
    iframe.setAttribute('title', 'Avatutor — conversación con tutor virtual');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    document.body.appendChild(iframe);

    if (cfg.position === 'modal') {
      backdrop = document.createElement('div');
      backdrop.className = 'avt-backdrop';
      backdrop.addEventListener('click', close);
      document.body.appendChild(backdrop);
    }
  }

  // --- 6. ACTIONS ---
  function open() {
    if (destroyed) return;

    // §13.5.2 (Opción A): el loader NO pide getUserMedia. El permiso de
    // micrófono lo solicita Embed.vue dentro del iframe al clickear
    // "Iniciar conversación", evitando la doble petición y dejando el
    // prompt del browser visible en el contexto correcto.
    if (!iframe) createIframe();
    iframe.classList.add('avt-open');
    if (backdrop) backdrop.classList.add('avt-open');
    hideFab();
    isOpen = true;
    dispatch('open');
  }

  function close() {
    if (iframe) iframe.classList.remove('avt-open');
    if (backdrop) backdrop.classList.remove('avt-open');
    showFab();
    isOpen = false;
    dispatch('close');
  }

  // Hide del FAB con doble red de seguridad: class (CSS !important) + inline
  // style.setProperty con prio "important" — el segundo gana siempre, incluso
  // si el host inyecta reglas hostiles sobre .avt-fab.avt-hidden.
  function hideFab() {
    if (!fab) return;
    fab.classList.add('avt-hidden');
    fab.style.setProperty('display', 'none', 'important');
  }

  function showFab() {
    if (!fab) return;
    fab.classList.remove('avt-hidden');
    fab.style.removeProperty('display');
  }

  function toggle() { isOpen ? close() : open(); }

  function dispatch(kind, payload) {
    const detail = payload || {};
    try {
      (listeners[kind] || []).forEach((fn) => {
        try { fn(detail); } catch (e) { console.warn(`[avatutor] listener ${kind} threw`, e); }
      });
    } catch (e) { /* swallow */ }
    try {
      document.dispatchEvent(new CustomEvent(`avatutor:${kind}`, { detail }));
    } catch (e) { /* IE/old browser fallback no aplica acá */ }
  }

  // Navegación HÍBRIDA (modo guía, TASK-009). El avatar decidió navegar la
  // página anfitriona. El widget vive en un iframe cross-origin, así que la
  // navegación real la ejecuta este loader sobre el DOM del host:
  //   - Si `target` es un selector/anchor presente en el host, hacemos scroll
  //     suave hacia él (sitios estáticos con anchors funcionan sin código del
  //     cliente).
  //   - SIEMPRE además disparamos CustomEvent('avatutor:navigate') (y los
  //     callbacks .on('navigate')) para que los clientes SPA conduzcan su
  //     propio router con el detail { target, sectionId, label }.
  function navigate(payload) {
    const detail = payload || {};
    const target = detail.target;
    if (target && typeof target === 'string') {
      let el = null;
      try {
        el = document.querySelector(target);
      } catch (err) {
        // selector inválido (p. ej. el avatar mandó una ruta SPA como
        // "/precios") — no es un selector CSS. El CustomEvent de abajo deja
        // que el cliente lo resuelva por su cuenta.
        el = null;
      }
      if (el && typeof el.scrollIntoView === 'function') {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
          el.scrollIntoView();
        }
      }
    }
    // Re-emitir a callbacks .on('navigate') + CustomEvent('avatutor:navigate').
    dispatch('navigate', detail);
  }

  // --- 7. postMessage bridge (widget → host) ---
  function onMessage(e) {
    if (e.origin !== SCRIPT_ORIGIN) return;
    const msg = e.data;
    if (!msg || typeof msg !== 'object' || msg.source !== 'avatutor-widget') return;

    const rawKind = String(msg.kind || '');
    if (!rawKind.startsWith('widget:')) return;
    const kind = rawKind.slice(7); // "widget:open" → "open"

    if (kind === 'ready') {
      isReady = true;
      // Defensive: si por alguna razón el FAB sigue visible al montar el
      // iframe (race condition, CSS hostil, llamada externa a open()),
      // forzar hide cuando el widget reporta ready o open.
      if (isOpen) hideFab();
    }
    if (kind === 'open' && isOpen) hideFab();
    if (kind === 'close') {
      close();
      return; // close() ya dispara 'close'
    }
    if (kind === 'navigate') {
      // Modo guía (TASK-009) — navegación HÍBRIDA. El avatar pidió llevar al
      // usuario a una sección de la página anfitriona.
      navigate(msg.payload);
      return; // navigate() ya dispara 'navigate' vía dispatch
    }
    dispatch(kind, msg.payload);
  }

  window.addEventListener('message', onMessage);

  // --- 8. API pública ---
  window.AvatutorWidget = {
    open,
    close,
    toggle,
    isOpen:  () => isOpen,
    isReady: () => isReady,
    config:  () => ({ ...cfg, iframeUrl: IFRAME_URL.toString() }),
    on: function (kind, fn) {
      if (!listeners[kind]) {
        console.warn(`[avatutor] evento desconocido: ${kind}. Válidos: ${Object.keys(listeners).join(', ')}`);
        return;
      }
      if (typeof fn !== 'function') return;
      listeners[kind].push(fn);
    },
    off: function (kind, fn) {
      if (!listeners[kind]) return;
      const i = listeners[kind].indexOf(fn);
      if (i >= 0) listeners[kind].splice(i, 1);
    },
    destroy: function () {
      destroyed = true;
      window.removeEventListener('message', onMessage);
      if (iframe)   { iframe.remove();   iframe = null; }
      if (fab)      { fab.remove();      fab = null; }
      if (backdrop) { backdrop.remove(); backdrop = null; }
      if (styleTag) { styleTag.remove(); styleTag = null; }
      Object.keys(listeners).forEach((k) => { listeners[k] = []; });
    },
  };

  // --- 9. BOOT ---
  function boot() {
    injectStyles();
    if (cfg.position === 'fullscreen' || cfg.launch === 'auto') {
      // fullscreen no necesita FAB (queda tapado igual). auto → abrir directo.
      if (cfg.launch === 'auto') open();
      else createFab();
    } else {
      createFab();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
